import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../awsAuth';
import './HomeNotificationsPanel.scss';

const DEFAULT_AVATAR =
  'https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png';

// Same /api/notifications endpoint and per-type text as
// src/components/Notifications/Notifications.js (renderText) — the common
// social types get the same short label here; everything else (bookings,
// moderation, etc.) falls back to the server-provided n.title/n.body, the
// exact same fallback Notifications.js itself uses for those types, instead
// of a made-up generic line. Full detail is one click away via "Усі".
function renderShortText(n) {
  const data = n.data || {};
  const who =
    data.actorName ||
    data.followerName ||
    data.actorUsername ||
    data.followerUsername ||
    data.birthdayUser?.username ||
    'Користувач';

  switch (n.type) {
    case 'like':
      return <><strong>{who}</strong> вподобав(ла) ваш допис.</>;
    case 'comment':
      return <><strong>{who}</strong> залишив(ла) коментар.</>;
    case 'mention':
      return <><strong>{who}</strong> згадав(ла) вас у коментарі.</>;
    case 'follow':
      return <><strong>{who}</strong> почав(ла) за вами стежити.</>;
    case 'birthday':
      return <><strong>{data.birthdayUser?.username || 'Користувач'}</strong> святкує день народження.</>;
    default:
      if (n.title) return n.body ? <>{n.title} — {n.body}</> : n.title;
      return 'Нове сповіщення';
  }
}

function formatRelativeTime(ts) {
  if (!ts) return '';
  const diffMs = Date.now() - Number(ts);
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'зараз';
  if (min < 60) return `${min} хв`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} год`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} д`;
  return new Date(Number(ts)).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' });
}

const HomeNotificationsPanel = () => {
  const { userId, isLoggedIn } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn || !userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const token = await getAccessToken();
        const res = await axios.get('/api/notifications', {
          params: { limit: 3 },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const raw = Array.isArray(res.data?.items) ? res.data.items : [];
        if (!cancelled) setItems(raw.slice(0, 3));
      } catch (e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userId]);

  if (!isLoggedIn) return null;

  return (
    <aside className="home-notifs" aria-label="Сповіщення">
      <div className="home-notifs__head">
        <span className="home-notifs__title">Сповіщення</span>
        <Link to="/notifications" className="home-notifs__all">Усі</Link>
      </div>

      {loading ? (
        <div className="home-notifs__empty">Завантаження…</div>
      ) : items.length === 0 ? (
        <div className="home-notifs__empty">Поки немає сповіщень</div>
      ) : (
        <div className="home-notifs__list">
          {items.map((n) => {
            const data = n.data || {};
            const avatar = data.actorAvatar || data.followerAvatar || data.birthdayUser?.avatar || null;

            return (
              <Link key={n.id} to="/notifications" className="home-notifs__row">
                <img
                  src={avatar || DEFAULT_AVATAR}
                  alt=""
                  className="home-notifs__avatar"
                  loading="lazy"
                />

                <div className="home-notifs__meta">
                  <div className="home-notifs__text">{renderShortText(n)}</div>
                  {n.createdAt ? (
                    <div className="home-notifs__time">{formatRelativeTime(n.createdAt)}</div>
                  ) : null}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </aside>
  );
};

export default HomeNotificationsPanel;
